interface EventDetails {
  id: string;
  display_name: string;
  slug: string;
  description: string;
  start_datetime: string;
  end_datetime: string;
  created_datetime: string;
  created_by: {
    id: string;
    display_name: string;
    profile_image_url: string;
    country: string;
    region: string;
    location_label: string;
    location_long: string;
    location_lat: string;
    verified: boolean;
  };
  modified_datetime: string;
  modified_by: {
    id: string;
    display_name: string;
    profile_image_url: string;
    country: string;
    region: string;
    location_label: string;
    location_long: string;
    location_lat: string;
    verified: boolean;
  };
  cancelled_datetime?: string;
  cancelled_by?: {
    id: string;
    display_name: string;
    profile_image_url: string;
    country: string;
    region: string;
    location_label: string;
    location_long: string;
    location_lat: string;
    verified: boolean;
  };
  visibility: string;
  team_id: string;
  team_slug: string;
  team_display_name: string;
  team_auto_invite: boolean;
  team_image_id: string;
  team_member: boolean;
  team_upgraded: boolean;
  live_now: boolean;
  ended: boolean;
  image: {
    id: string;
    image_url: string;
  };
  slot_list: Array<{
    id: string;
    train_event_id: string;
    train_event_stage_id: string;
    reservation_id: string;
    start_datetime: string;
    end_datetime: string;
    user_channel?: {
      id: string;
      user_id: string;
      display_name: string;
      platform: string;
      description: string;
      profile_image_url: string;
      added_datetime: string;
    };
    user?: {
      id: string;
      display_name: string;
      profile_image_url: string;
      country: string;
      region: string;
      location_label: string;
      location_long: string;
      location_lat: string;
      verified: boolean;
    };
    reserved: boolean;
    cancelled: boolean;
    slot_live: boolean;
    minutes_until_start: number;
    time_to_checkin: boolean;
    checkedin_datetime?: string;
    notes?: string;
  }>;
}

interface ActiveSlot {
  event_id: string;
  event_name: string;
  event_slug: string;
  event_image_id: string;
  active_slot_id: string;
  active_slot_start_datetime: string;
  active_slot_end_datetime: string;
  next_slot_start_datetime: string | null;
  next_slot_end_datetime: string | null;
  active_channel_name: string;
  active_channel_platform_user_id: string;
  ondeck_channel_name: string;
  ondeck_channel_platform_user_id: string;
  train_event_id?: string; // Add as optional field
}

interface UpcomingSlot {
  event_id: string;
  train_event_id?: string;
  event_name: string;
  event_slug: string;
  event_image_id: string;
  channel_id: string;
  channel_name: string;
  slot_start_datetime: string;
  slot_end_datetime: string;
  slotId: string; // Add this property to match usage
}

interface EventsResponse {
  active_slot: ActiveSlot;
  upcoming_slots: UpcomingSlot[];
}

interface TeammateData {
  user_id: string;
  user_display_name: string;
  teammate_user_id: string;
  teammate_role: string;
  teammate_display_name: string;
  teammate_profile_image_url: string;
  teammate_platform_user_id: string;
  teammate_is_live: boolean;
  team_id: string;
  team_name: string;
  team_image_id: string;
}

interface TeammatesResponse {
  data: TeammateData[];
}

class R3LabsService {
  private readonly _baseUrl: string;

  constructor() {
    this._baseUrl = import.meta.env.VITE_R3DLABS_BASE_URL || 'http://localhost:8080';
  }

  public get baseUrl(): string {
    return this._baseUrl;
  }

  async getEventDetails(eventId: string): Promise<EventDetails> {
    try {
      const url = `${this._baseUrl}/train-events/${encodeURIComponent(eventId)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(`Failed to fetch event details: ${response.status} ${response.statusText}\nResponse: ${responseText}`);
      }

      try {
        const data = JSON.parse(responseText);
        return data;
      } catch (parseError) {
        console.error('Failed to parse event details response:', parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      throw error;
    }
  }

  async getUserEvents(channelName: string): Promise<EventsResponse> {
    try {
      const url = `${this._baseUrl}/views/user-outlook?channel_name=${encodeURIComponent(channelName)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      });

      const responseText = await response.text();
      console.log('Raw user events response:', responseText);

      if (!response.ok) {
        throw new Error(`Failed to fetch events: ${response.status} ${response.statusText}\nResponse: ${responseText}`);
      }

      try {
        const data = JSON.parse(responseText);
        console.log('Parsed user events:', {
          has_active_slot: Boolean(data.active_slot),
          active_slot: data.active_slot ? {
            id: data.active_slot.id,
            event_id: data.active_slot.event_id,
            train_event_id: data.active_slot.train_event_id,
            name: data.active_slot.event_name
          } : null,
          upcoming_slots: data.upcoming_slots?.map((slot: UpcomingSlot) => ({
            slotId: slot.slotId,
            event_id: slot.event_id,
            train_event_id: slot.train_event_id,
            name: slot.event_name
          }))
        });
        return data;
      } catch (parseError) {
        console.error('Failed to parse user events response:', parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  async getUserTeammates(displayName: string, teammateIsLive?: boolean): Promise<TeammatesResponse> {
    try {
      let url = `${this._baseUrl}/views/user-teammates?display_name=${encodeURIComponent(displayName)}`;
      
      // Only add the teammate_is_live parameter if explicitly requested
      if (teammateIsLive === true) {
        url += `&teammate_is_live=true`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      });

      const responseText = await response.text();
      console.log('Raw user teammates response:', responseText);

      if (!response.ok) {
        throw new Error(`Failed to fetch teammates: ${response.status} ${response.statusText}\nResponse: ${responseText}`);
      }

      try {
        const data = JSON.parse(responseText);
        console.log('Parsed user teammates:', {
          count: data.data?.length || 0,
          teammates: data.data?.map((teammate: TeammateData) => ({
            display_name: teammate.teammate_display_name,
            platform_user_id: teammate.teammate_platform_user_id,
            team_name: teammate.team_name,
            is_live: teammate.teammate_is_live
          }))
        });
        return data;
      } catch (parseError) {
        console.error('Failed to parse user teammates response:', parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
      }
    } catch (error) {
      console.error('Error fetching teammates:', error);
      throw error;
    }
  }
}

export const r3LabsService = new R3LabsService();
export type { EventsResponse, ActiveSlot, UpcomingSlot, EventDetails, TeammateData, TeammatesResponse };
